import React, { useCallback } from 'react';
import { Form, Icon } from 'antd';
import PropTypes from 'prop-types';
import { getSignupData } from '../utils/localStorage';
import { validateEmail, validateName } from '../utils/validation';
import {
    PageShell,
    FormCard,
    StepBadge,
    CardTitle,
    CardSubtitle,
    FieldLabel,
    LoginHint,
    StyledInput,
    StyledButton,
} from '../styled';

const UserForm = ({
    form: { getFieldDecorator, validateFields, setFields },
    setSignupData,
    setProgress,
    validationErrors = {}
}) => {

    const handleSubmit = (e) => {
        e.preventDefault();

        validateFields((err, values) => {
            if (err) return;

            // Valida já aqui o que antes só estourava no fim do fluxo
            // (e devolvia o usuário para este passo depois de criar a senha)
            const emailError = validateEmail(values.email);
            const nameError = validateName(values.name);
            const whatsappDigits = (values.whatsapp || '').replace(/\D/g, '');
            const whatsappError = whatsappDigits.length < 10
                ? 'Informe um número de WhatsApp válido com DDD'
                : null;

            if (emailError || nameError || whatsappError) {
                if (emailError) setFields({ email: { value: values.email, errors: [new Error(emailError)] } });
                if (nameError) setFields({ name: { value: values.name, errors: [new Error(nameError)] } });
                if (whatsappError) setFields({ whatsapp: { value: values.whatsapp, errors: [new Error(whatsappError)] } });
                return;
            }

            const existingData = getSignupData();
            const updatedData = { ...existingData, ...values };
            setSignupData(updatedData);
            setProgress(80);
        });
    };

    const formatWhatsApp = useCallback((e) => {
        const { value } = e.target;
        const formattedValue = value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/g, '($1) $2')
            .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
        e.target.value = formattedValue;
    }, []);

    const initialSignupData = getSignupData();

    return (
        <PageShell>
            <FormCard>
                <StepBadge>Passo 1 de 2</StepBadge>
                <CardTitle>Cadastre-se gratuitamente</CardTitle>
                <CardSubtitle>Comece a utilizar em minutos — sem cartão de crédito.</CardSubtitle>

                <Form onSubmit={handleSubmit}>
                    <FieldLabel htmlFor="company_name">Nome da contabilidade</FieldLabel>
                    <Form.Item
                        validateStatus={validationErrors.company_name ? 'error' : ''}
                        help={validationErrors.company_name}
                    >
                        {getFieldDecorator('company_name', {
                            initialValue: initialSignupData.company_name,
                            rules: [{ required: true, message: 'Informe o nome da sua contabilidade' }],
                        })(
                            <StyledInput
                                id="company_name"
                                type="text"
                                autoFocus
                                placeholder="Ex.: Silva Contabilidade"
                                prefix={<Icon type="bank" />}
                            />
                        )}
                    </Form.Item>

                    <FieldLabel htmlFor="name">Seu nome completo</FieldLabel>
                    <Form.Item
                        validateStatus={validationErrors.name ? 'error' : ''}
                        help={validationErrors.name}
                    >
                        {getFieldDecorator('name', {
                            initialValue: initialSignupData.name,
                            rules: [{ required: true, message: 'Informe seu nome completo' }],
                        })(
                            <StyledInput id="name" type="text" placeholder="Ex.: Maria Silva" prefix={<Icon type="user" />} />
                        )}
                    </Form.Item>

                    <FieldLabel htmlFor="email">E-mail profissional</FieldLabel>
                    <Form.Item
                        validateStatus={validationErrors.email ? 'error' : ''}
                        help={validationErrors.email}
                    >
                        {getFieldDecorator('email', {
                            initialValue: initialSignupData.email,
                            rules: [{ required: true, message: 'Informe seu e-mail' }],
                        })(
                            <StyledInput id="email" type="email" placeholder="voce@suaempresa.com.br" prefix={<Icon type="mail" />} />
                        )}
                    </Form.Item>

                    <FieldLabel htmlFor="whatsapp">WhatsApp</FieldLabel>
                    <Form.Item
                        validateStatus={validationErrors.whatsapp ? 'error' : ''}
                        help={validationErrors.whatsapp}
                    >
                        {getFieldDecorator('whatsapp', {
                            initialValue: initialSignupData.whatsapp,
                            rules: [{ required: true, message: 'Informe seu número de WhatsApp' }],
                        })(
                            <StyledInput
                                id="whatsapp"
                                type="text"
                                placeholder="(11) 98765-4321"
                                prefix={<Icon type="phone" />}
                                maxLength={15}
                                onChange={formatWhatsApp}
                            />
                        )}
                    </Form.Item>

                    <StyledButton type="primary" htmlType="submit" block onClick={handleSubmit}>
                        Continuar <Icon type="arrow-right" />
                    </StyledButton>
                </Form>

                <LoginHint>
                    Já tem uma conta? <a href="/login">Entrar</a>
                </LoginHint>
            </FormCard>
        </PageShell>
    )
};

UserForm.propTypes = {
    form: PropTypes.shape({
        getFieldDecorator: PropTypes.func.isRequired,
        validateFields: PropTypes.func.isRequired,
        setFields: PropTypes.func.isRequired,
    }).isRequired,
    setSignupData: PropTypes.func.isRequired,
    setProgress: PropTypes.func.isRequired,
    validationErrors: PropTypes.object,
};

export default Form.create({ name: 'onboarding' })(UserForm);
